import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Crown, Trophy, ArrowUpRight } from 'lucide-react';
import api from '../../lib/api';
import { BravxRoleBadges } from '../BravxRoleBadges';

const GOLD = '#D4AF37';

/**
 * Home teaser for the BRAVX leaderboard. Pulls /bravx/leaderboard?period=month
 * (fallback to all-time if the month is empty) and shows top 5 in a luxe card.
 */
export default function BravxLeaderboardTeaser() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // Try monthly first; fallback to "all" if empty
        const monthRes = await api.get('/bravx/leaderboard?period=month&limit=5');
        const monthData = monthRes.data || {};
        if (mounted && (monthData.entries?.length || 0) > 0) {
          setData(monthData);
        } else {
          const allRes = await api.get('/bravx/leaderboard?period=all&limit=5');
          if (mounted) setData(allRes.data);
        }
      } catch (_) {
        if (mounted) setData({ entries: [], label: '' });
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const entries = data?.entries || [];
  const top = entries[0];
  const isEmpty = entries.length === 0;

  if (loading) return null;
  // We DO render even when empty — production user wants to see the section
  // and have it work as an invitation to join, instead of disappearing.

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      className="relative rounded-3xl overflow-hidden border border-[#D4AF37]/35"
      style={{
        background: 'linear-gradient(135deg, rgba(212,175,55,0.08), rgba(0,0,0,0.6) 60%)',
      }}
      data-testid="home-bravx-teaser"
    >
      {/* Background glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 -left-20 w-[420px] h-[420px] rounded-full blur-[140px]" style={{ background: `${GOLD}33` }} />
        <div className="absolute -bottom-24 right-0 w-[360px] h-[360px] rounded-full blur-[130px]" style={{ background: `${GOLD}22` }} />
      </div>

      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-6 sm:gap-10 p-6 sm:p-10">
        {/* Left: pitch */}
        <div className="flex flex-col justify-between">
          <div>
            <p
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.3em] font-bold text-[#D4AF37] mb-3"
              style={{ fontFamily: "'Unbounded', sans-serif" }}
            >
              <Sparkles size={11} /> Sistema BRAVX
            </p>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-[0.95] uppercase text-white"
              style={{ fontFamily: "'Unbounded', sans-serif" }}
            >
              El ranking de los que <span className="text-[#D4AF37]">mueven</span> la escena
            </h2>
            <p className="mt-4 text-sm sm:text-base text-white/65 max-w-md">
              Top 10 <strong className="text-white">@usernames</strong> con más puntos BRAVX{data?.label ? ` · ${data.label}` : ''}. Sumás comprando entradas y cada vez que alguien usa tu @ en el checkout.
            </p>
          </div>

          <div className="flex flex-wrap gap-3 mt-6">
            <Link
              to="/bravx/top"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#D4AF37] hover:bg-[#F5D25A] text-black text-xs font-extrabold uppercase tracking-wider transition-colors"
              style={{ fontFamily: "'Unbounded', sans-serif" }}
              data-testid="home-bravx-cta-view-top"
            >
              <Trophy size={13} /> Ver Top BRAVX
            </Link>
            <Link
              to="/cuenta?tab=bravx"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 hover:bg-white/10 text-white text-xs font-bold uppercase tracking-wider transition-colors"
              data-testid="home-bravx-cta-claim"
            >
              <Sparkles size={13} /> Reclamar mi @
            </Link>
          </div>
        </div>

        {/* Right: top 5 list (or empty-state invite when production has no entries yet) */}
        <div className="space-y-2 sm:space-y-2.5" data-testid="home-bravx-list">
          {isEmpty ? (
            <div className="rounded-2xl border border-dashed border-[#D4AF37]/30 bg-white/[0.02] p-6 sm:p-8 text-center">
              <Crown size={28} className="mx-auto text-[#D4AF37] mb-3" />
              <p className="text-base font-bold text-white mb-1">El ranking está esperando líder</p>
              <p className="text-xs text-white/55 leading-relaxed">
                Sé el primero en reclamar tu @ y empezá a sumar puntos cada vez que un amigo use tu nombre al comprar entradas.
              </p>
              <Link
                to="/cuenta?tab=bravx"
                className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#D4AF37] hover:bg-[#F5D25A] text-black text-[11px] font-extrabold uppercase tracking-wider transition-colors"
              >
                <Sparkles size={11} /> Reclamar mi @
              </Link>
            </div>
          ) : (
            entries.slice(0, 5).map((it, idx) => {
              const hasHandle = !!it.username;
              const initial = (it.username || it.display_name || '?').slice(0, 1).toUpperCase();
              const className = `flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-2.5 rounded-xl border transition-all group ${
                idx === 0
                  ? 'bg-[#D4AF37]/10 border-[#D4AF37]/40'
                  : 'bg-white/[0.03] border-white/10 ' + (hasHandle ? 'hover:border-white/25' : '')
              }`;
              const Inner = (
                <>
                  <div
                    className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-black ${
                      idx === 0 ? 'bg-[#D4AF37] text-black' : 'bg-white/10 text-white/70'
                    }`}
                  >
                    {idx === 0 ? <Crown size={14} /> : `#${idx + 1}`}
                  </div>
                  {it.avatar ? (
                    <img src={it.avatar} alt="" className="w-9 h-9 rounded-full object-cover border border-white/15" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-white/10 border border-white/15 flex items-center justify-center text-xs font-bold text-white/80">
                      {initial}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    {hasHandle ? (
                      <p className="text-sm font-bold text-white truncate">@{it.username}</p>
                    ) : (
                      <p className="text-sm font-bold text-white truncate">{it.display_name || 'Usuario'}</p>
                    )}
                    {hasHandle && it.display_name && (
                      <p className="text-[11px] text-white/40 truncate">{it.display_name}</p>
                    )}
                    {!hasHandle && (
                      <p className="text-[10px] uppercase tracking-wider text-[#D4AF37]/70">Sin @ reclamado</p>
                    )}
                    <BravxRoleBadges entry={it} className="mt-1" />
                  </div>
                  <div className="text-right">
                    <p
                      className={`text-base sm:text-lg font-black ${idx === 0 ? 'text-[#D4AF37]' : 'text-white'}`}
                      style={{ fontFamily: "'Unbounded', sans-serif" }}
                    >
                      {it.points}
                    </p>
                    <p className="text-[9px] uppercase tracking-wider text-white/40">pts</p>
                  </div>
                  {hasHandle && <ArrowUpRight size={14} className="text-white/30 group-hover:text-[#D4AF37] transition-colors" />}
                </>
              );
              return hasHandle ? (
                <Link
                  key={`${it.username}-${idx}`}
                  to={`/u/${it.username}`}
                  className={className}
                  data-testid={`home-bravx-row-${idx + 1}`}
                >
                  {Inner}
                </Link>
              ) : (
                <div
                  key={`anon-${idx}`}
                  className={className}
                  data-testid={`home-bravx-row-${idx + 1}`}
                >
                  {Inner}
                </div>
              );
            })
          )}
        </div>
      </div>

      {top && (
        <div className="relative z-10 px-6 sm:px-10 pb-6 sm:pb-8">
          <Link
            to="/bravx/top"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-bold text-white/50 hover:text-white transition-colors"
            data-testid="home-bravx-cta-bottom"
          >
            Ver ranking completo <ArrowUpRight size={12} />
          </Link>
        </div>
      )}
    </motion.div>
  );
}
